<template>
  <form-section :title="title">
    <template slot="controls">
      <content-actions
        v-model="value"
        :channel="$route.query.channel"
        apply-template-enabled
        copy-enabled
        :model="model"
      />
      <slot name="selected-template-info"></slot>
    </template>
    <el-row>
      <el-col :lg="10" :xl="8">
        <rest-form-item
          prop="inbox_type"
          :label="$t('CampaignForm_Inbox_Type')"
        >
          <el-select
            v-model="value.inbox_type"
            inline
            data-qa="inbox-type-dropdown"
          >
            <el-option
              :key="0"
              :value="0"
              :label="$t('CampaignForm_Inbox_Type_Alert')"
              data-qa="inbox-type-alert"
              border
            />
            <el-option
              :key="1"
              :value="1"
              :label="$t('CampaignForm_Inbox_Type_Card')"
              data-qa="inbox-type-card"
              border
            />
          </el-select>
        </rest-form-item>
        <el-tabs v-model="activeTab" :stretch="true">
          <el-tab-pane
            name="content"
            :label="$t('CampaignForm_Inbox_Tab_Content')"
          >
            <form-item
              prop="push_title"
              :label="$t('CampaignForm_Push_Field_Subject')"
            >
              <redactor
                :key="titleKey"
                v-model="value.push_title"
                :custom-config="editorConfig"
                :buttons="editorButtons"
                :plugins="editorPlugins"
                :use-default-plugins="false"
                :use-default-buttons="false"
                data-qa="inbox-message-title-input"
              />
            </form-item>

            <!-- Message Text -->
            <form-item
              prop="push_text"
              :label="$t('CampaignForm_Push_Field_Text')"
            >
              <redactor
                :key="textKey"
                v-model="value.push_text"
                :custom-config="editorConfig"
                :buttons="editorButtons"
                :plugins="editorPlugins"
                :use-default-plugins="false"
                :use-default-buttons="false"
                data-qa="inbox-message-text-input"
              />
            </form-item>

            <!-- Voucher code settings -->
            <template v-if="model.voucher_codes_enabled">
              <form-item
                prop="voucher_code_enabled"
                :label="$t('CampaignForm_VoucherType_Title')"
              >
                <enum-select
                  v-model="value.voucher_code_enabled"
                  :options="voucherTypesLabels"
                  :number="true"
                  data-qa="voucher_code_enabled"
                />
              </form-item>

              <form-item
                prop="voucher_code_position"
                :label="$t('CampaignForm_VoucherPosition_Title')"
                v-if="value.voucher_code_enabled === 1"
              >
                <enum-select
                  v-model="value.voucher_code_position"
                  :options="voucherPositionsLabels"
                  :number="true"
                  data-qa="voucher_code_position"
                />
              </form-item>
            </template>

            <!-- Default Action -->
            <form-item :label="$t('CampaignForm_Push_DefaultAction')">
              <el-select
                v-model="value.push_action.action"
                @change="updateDefaultAction"
                data-qa="inbox-default-action-dropdown"
              >
                <el-option
                  value=""
                  :label="$t('CampaignForm_Push_DefaultAction_None')"
                  data-qa="inbox-action-none"
                />
                <el-option
                  value="url"
                  :label="$t('CampaignForm_Push_DefaultAction_Url')"
                  data-qa="inbox-action-open-url"
                />
                <el-option
                  value="deeplink"
                  :label="$t('CampaignForm_Push_DefaultAction_Deeplink')"
                  data-qa="inbox-action-go-to-deeplink"
                />
                <el-option
                  v-if="hasInApp"
                  value="inapp"
                  :label="$t('CampaignForm_Push_DefaultAction_InAppMessage')"
                  data-qa="inbox-action-in-app-message"
                />
              </el-select>
            </form-item>
            <form-item
              v-if="value.push_action.action === 'url'"
              :label="$t('CampaignForm_Push_DefaultAction_Url')"
            >
              <el-input
                v-model="value.push_action.url"
                data-qa="inbox-url-input"
              />
            </form-item>
            <form-item v-if="value.push_action.action === 'url'">
              <checkbox
                v-model="value.push_action.url_blank"
                :title="$t('CampaignForm_Push_URL_BlankPage')"
                data-qa="inbox-open-new-tab-checkbox"
              />
            </form-item>
            <form-item
              v-if="value.push_action.action === 'deeplink'"
              :label="$t('CampaignForm_Push_DefaultAction_Deeplink')"
            >
              <el-input
                v-model="value.push_action.deeplink"
                data-qa="inbox-deeplink-input"
              />
            </form-item>

            <!-- Image upload -->
            <form-item
              prop="push_icon"
              class="push-icon-ct"
              :label="$t('CampaignForm_UploadType_Upload')"
            >
              <campaign-image-upload
                :value="value.push_icon"
                :file.sync="value.push_icon"
                :message-type="channelTypes.inbox"
              />
            </form-item>

            <!-- Payload -->
            <form-item
              prop="payload_add"
              :label="$t('CampaignForm_Push_Payload')"
            >
              <template v-for="(payload, index) in value.payload_add">
                <el-row :key="index" class="payload-row">
                  <el-col :span="10">
                    <form-item :prop="value.payload_add[index].key">
                      <el-input
                        v-model="value.payload_add[index].key"
                        :placeholder="$t('CampaignForm_Push_PayloadKey')"
                      />
                    </form-item>
                  </el-col>
                  <el-col :span="10">
                    <form-item :prop="value.payload_add[index].value">
                      <el-input
                        v-model="value.payload_add[index].value"
                        :placeholder="$t('CampaignForm_Push_PayloadValue')"
                      />
                    </form-item>
                  </el-col>
                  <el-col :span="2">
                    <icon
                      class="icon"
                      glyph="bin"
                      @click="removePayload(index)"
                    />
                  </el-col>
                </el-row>
              </template>
              <div>
                <el-button
                  type="primary"
                  plain
                  @click="addPayload"
                  :disabled="isPayloadBtnDisabled()"
                  data-qa="add-payload-btn"
                >
                  {{ $t('CampaignForm_Push_AddPayload') }}
                </el-button>
              </div>
            </form-item>
          </el-tab-pane>
          <el-tab-pane
            name="style"
            :label="$t('CampaignForm_Inbox_Tab_Style')"
            v-if="value.inbox_type === 1"
          >
            <el-row>
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inbox_style.bg"
                  :label-text="$t('CampaignForm_Inbox_Style_CardBackground')"
                  data-qa="card-background-color-input"
                />
              </el-col>
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inbox_style.title_bg"
                  :label-text="$t('CampaignForm_Inbox_Style_TitleBackground')"
                  data-qa="title-background-color-input"
                />
              </el-col>
            </el-row>
          </el-tab-pane>
        </el-tabs>
      </el-col>
      <el-col :lg="14" :xl="16" class="preview">
        <inbox-message-preview
          :message="formattedMessage"
          :message-type="value.inbox_type"
          :message-style="messageStyles"
          :global-styles="globalStyles"
        />
      </el-col>
    </el-row>
  </form-section>
</template>

<script>
import RestFormItem from '@/common/components/rest/RestFormItem'
import FormItem from '@/common/components/form/FormItem'
import ColorPickerField from '@/common/components/form/ColorPickerField'
import FormSection from '@/common/components/form/FormSection'
import { EventBus } from '@/core/EventBus'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import Redactor from '@/common/components/Redactor'
import Checkbox from '@/common/components/form/Checkbox'
import CampaignImageUpload from '@/modules/campaign-manage/components/content/CampaignImageUpload'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import ContentActions from '@/modules/campaign-manage/components/ContentActions'
import { voucherTypesLabels } from '@/modules/campaign-manage/enum/VoucherTypesEnum'
import {
  voucherPositions,
  voucherPositionsLabels
} from '@/modules/campaign-manage/enum/VoucherPositionsEnum'
import EnumSelect from '@/common/components/form/EnumSelect'

export default {
  name: 'Inbox',
  components: {
    ContentActions,
    CampaignImageUpload,
    Checkbox,
    Redactor,
    FormSection,
    ColorPickerField,
    FormItem,
    RestFormItem,
    EnumSelect
  },
  props: {
    title: {
      type: String
    },
    value: {
      type: Object,
      required: true
    },
    model: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      titleKey: '',
      textKey: '',
      channelTypes,
      voucherTypesLabels,
      voucherPositions,
      voucherPositionsLabels,
      defaultAction: '',
      activeTab: 'content',
      editorConfig: {
        tabKey: false,
        enterKey: true,
        minHeight: 50,
        maxHeight: 350
      },
      editorButtons: ['bold', 'italic', 'underline'],
      editorPlugins: [
        'alignment',
        'fontsize',
        'fontcolor',
        'fontfamily',
        'emojis',
        'personalization',
        'snippet'
      ]
    }
  },
  beforeMount() {
    if (this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE) {
      this.editorPlugins.push('optimove')
    }
    if (this.model.trigger === campaignTriggerTypes.TRIGGER_SALESFORCE) {
      this.editorPlugins.push('salesforce')
    }
  },
  mounted() {
    EventBus.$on('applyPushTemplate-' + this.key, data => {
      this.titleKey = new Date().getTime() + '_title'
      this.textKey = new Date().getTime() + '_text'
    })
    if (
      this.value.push_action &&
      this.value.push_action.hasOwnProperty('action')
    ) {
      this.defaultAction = this.value.push_action.action
    }
  },
  methods: {
    copyFrom() {
      EventBus.$emit('copyFromDialog', channelTypes.inbox)
    },
    updateDefaultAction(value) {
      if (value === 'url') {
        delete this.value.push_action.deeplink
        this.$set(this.value.push_action, 'url', '')
      }

      if (value === 'deeplink') {
        delete this.value.push_action.url
        this.$set(this.value.push_action, 'deeplink', '')
      }

      if (value === '') {
        delete this.value.push_action.url
        delete this.value.push_action.deeplink
      }

      this.value.push_action.action = value

      if (value === 'inapp') {
        delete this.value.push_action.url
        delete this.value.push_action.deeplink
      }
    },

    addPayload() {
      if (this.value.payload_add === null) {
        this.value.payload_add = []
      }
      this.value.payload_add.push({ key: '', value: '' })
    },
    isPayloadBtnDisabled() {
      if (this.value.payload_add === null) {
        return false
      }
      if (!this.value.payload_add[this.value.payload_add.length - 1]) {
        return false
      }

      return (
        this.value.payload_add[this.value.payload_add.length - 1].key === '' ||
        this.value.payload_add[this.value.payload_add.length - 1].value === ''
      )
    },
    removePayload(index) {
      if (
        this.value.payload_add[index].key === '' &&
        this.value.payload_add[index].value === ''
      ) {
        this.value.payload_add.splice(index, 1)

        return
      }

      this.$msgbox({
        title: this.$t('Common_Popup_Delete_Title'),
        message: this.$t('Common_Popup_Delete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Common_Popup_Delete_Confirm'),
        cancelButtonText: this.$t('Common_Popup_Delete_Cancel'),
        center: true
      })
        .then(() => {
          this.value.payload_add.splice(index, 1)
        })
        .catch(() => {})
    },

    removeIcon() {
      this.$msgbox({
        title: this.$t('Common_Popup_Delete_Title'),
        message: this.$t('Common_Popup_Delete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Common_Popup_Delete_Confirm'),
        cancelButtonText: this.$t('Common_Popup_Delete_Cancel'),
        center: true
      })
        .then(() => {
          this.value.push_icon = null
        })
        .catch(() => {})
    }
  },
  computed: {
    key() {
      return `${this.$route.query.channel}_${this.$route.query.variant}_${this.$route.query.lang}`
    },
    hasInApp() {
      return this.model.inapp
    },
    formattedMessage() {
      let text =
        this.value.push_text ||
        (this.value.inbox_type === 1
          ? this.$t('CampaignForm_Push_Field_Text_Placeholder')
          : '')

      if (this.model.voucher_codes_enabled && this.value.voucher_code_enabled) {
        if (this.value.voucher_code_position === voucherPositions.TOP) {
          text = this.$t('CampaignForm_VoucherCode_Sample') + ' ' + text
        } else {
          text = text + ' ' + this.$t('CampaignForm_VoucherCode_Sample')
        }
      }

      return {
        title: this.value.push_title || '',
        text: text,
        icon: this.value.push_icon || ''
      }
    },
    messageStyles() {
      if (this.value.inbox_type === 0) {
        return {}
      }
      return {
        bg: this.value.inbox_style.bg,
        title_bg: this.value.inbox_style.title_bg
      }
    },
    globalStyles() {
      return {}
    }
  },
  watch: {
    'value.inbox_type': {
      handler(type) {
        if (type === 0) {
          this.activeTab = 'content'
        }
      }
    }
  }
}
</script>

<style scoped>
.push-icon-ct >>> .el-form-item__content {
  display: flex;
}

.icon-ct {
  align-self: center;
  margin-left: 10px;
}

.icon-bin {
  font-size: 18px;
  color: #a6a6a6;
}

.icon-bin:hover {
  cursor: pointer;
}

.icon {
  display: flex;
  justify-content: center;
  width: 25px;
  height: 40px;
  color: #a7acba;
  cursor: pointer;
}
.payload-row {
  margin-bottom: 15px;
}
.preview {
  background-color: #f8fafc;
  padding-top: 30px;
  padding-bottom: 30px;
  display: flex;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/channels/Inbox.vue